import React, { useEffect, useRef, useState } from "react";
import { Dialog } from "@mui/material";

interface Props {
    images: string[];
    open: boolean;
    startIndex: number;
    onClose: () => void;
}

const ImageLightbox: React.FC<Props> = ( { images, open, startIndex, onClose } ) => {
    const [ index, setIndex ] = useState( startIndex );

    const [ scale, setScale ] = useState( 1 );
    const [ position, setPosition ] = useState( { x: 0, y: 0 } );
    const imgRef = useRef<HTMLImageElement | null>( null );

    const [ dragging, setDragging ] = useState( false );
    const dragStart = useRef( { x: 0, y: 0 } );

    useEffect( () => {
        setIndex( startIndex );
        setScale( 1 );
        setPosition( { x: 0, y: 0 } );
    }, [ startIndex ] );

    const next = () => {
        setScale( 1 );
        setPosition( { x: 0, y: 0 } );
        setIndex( ( index + 1 ) % images.length );
    };

    const prev = () => {
        setScale( 1 );
        setPosition( { x: 0, y: 0 } );
        setIndex( ( index - 1 + images.length ) % images.length );
    };

    // 🔍 Mouse wheel zoom
    const handleWheel = ( e: React.WheelEvent ) => {
        e.preventDefault();

        let newScale = scale + ( e.deltaY < 0 ? 0.1 : -0.1 );
        if ( newScale < 1 ) newScale = 1;
        if ( newScale > 4 ) newScale = 4;

        setScale( newScale );
    };

    // 🖱 Drag Start
    const handleMouseDown = ( e: React.MouseEvent ) => {
        if ( scale === 1 ) return;

        setDragging( true );
        dragStart.current = {
            x: e.clientX - position.x,
            y: e.clientY - position.y,
        };
    };

    // 🖱 Drag Move
    const handleMouseMove = ( e: React.MouseEvent ) => {
        if ( !dragging ) return;

        setPosition( {
            x: e.clientX - dragStart.current.x,
            y: e.clientY - dragStart.current.y,
        } );
    };

    // 🖱 Drag End
    const handleMouseUp = () => setDragging( false );

    // 📱 Double click to zoom toggle
    const toggleZoom = () => {
        if ( scale === 1 ) {
            setScale( 2 );
        } else {
            setScale( 1 );
            setPosition( { x: 0, y: 0 } );
        }
    };

    return (
        <Dialog fullScreen open={ open } onClose={ onClose }>
            <div
                style={ {
                    height: "100vh",
                    width: "100vw",
                    background: "#000",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    position: "relative",
                    overflow: "hidden",
                } }
                onWheel={ handleWheel }
                onMouseMove={ handleMouseMove }
                onMouseUp={ handleMouseUp }
                onMouseLeave={ handleMouseUp }
            >
                {/* Close */ }
                <div
                    onClick={ onClose }
                    style={ {
                        position: "absolute",
                        top: 20,
                        right: 25,
                        color: "#fff",
                        fontSize: 35,
                        cursor: "pointer",
                    } }
                >
                    ✕
                </div>

                {/* Left Arrow */ }
                <div
                    onClick={ prev }
                    style={ {
                        position: "absolute",
                        left: 20,
                        color: "#fff",
                        fontSize: 40,
                        cursor: "pointer",
                        userSelect: "none",
                    } }
                >
                    ‹
                </div>

                {/* Image with zoom + drag */ }
                <img
                    ref={ imgRef }
                    src={ images[ index ] }
                    onMouseDown={ handleMouseDown }
                    onDoubleClick={ toggleZoom }
                    style={ {
                        maxHeight: "80vh",
                        maxWidth: "80vw",
                        borderRadius: 10,
                        cursor: scale > 1 ? "grab" : "zoom-in",
                        transform: `scale(${ scale }) translate(${ position.x / scale }px, ${ position.y / scale }px)`,
                        transition: dragging ? "none" : "transform 0.2s ease",
                    } }
                />

                {/* Right Arrow */ }
                <div
                    onClick={ next }
                    style={ {
                        position: "absolute",
                        right: 20,
                        color: "#fff",
                        fontSize: 40,
                        cursor: "pointer",
                        userSelect: "none",
                    } }
                >
                    ›
                </div>

                {/* Page Counter */ }
                <div
                    style={ {
                        position: "absolute",
                        bottom: 20,
                        fontSize: 18,
                        color: "#fff",
                        background: "rgba(0,0,0,0.5)",
                        padding: "6px 14px",
                        borderRadius: 20,
                    } }
                >
                    { index + 1 } / { images.length }
                </div>
            </div>
        </Dialog>
    );
};

export default ImageLightbox;
